<template>
  <div
    v-if="residence"
    itemscope
    itemtype="http://schema.org/Product"
  >
    <div class="c-top-nav-container">
      <router-link
        to="/"
        class="c-btn c-btn--primary c-btn--margins c-btn--has-icon"
      >
        <icon-left
          class="c-btn__icon c-btn__icon--left c-btn__icon--alignment"
        />
        Oversikt
      </router-link>
    </div>
    <section
      v-if="hasOptions === true && residence.availability.slug === 'for-sale'"
      :class="{
        'l-detailspage--options-nomedia': residence.images.length === 0,
        'l-detailspage--options-media': residence.images.length > 0,
      }"
      class="l-detailspage l-detailspage--options"
    >
      <section class="c-infosection">
        <div class="c-infosection__container">
          <!-- Show illustration, title and facts in first column -->
          <Illustration
            :images="residence.images"
            :residence_id="residence.id"
            class="l-detailspage__media"
          />
          <h1
            class="u-h2"
            itemprop="name"
          >
            {{ residence.name }}
          </h1>
          <Factlist
            v-if="hasLoadedAllData"
            :id="residence.id"
            :residencetype_id="residence.residencetype_id"
          />
          <spinner v-else />
          <button
            v-if="residence.images.length > 0"
            @click="toggleModal"
            @keyup.enter="toggleModal"
            class="c-btn c-btn--primary c-btn--margins"
          >
            Se større plantegninger
          </button>
          <modal-illustration
            v-if="showLargeIllustrations && residence.images.length > 0"
            @close="toggleLargeIllustrations"
          >
            <Illustration :residence_id="residence.id" />
          </modal-illustration>
          <LinkButton
            v-for="file in files"
            :key="file.id"
            :label="file.title"
            :url="file.url"
            :button-type="'secondary'"
          />
          <LinkButton
            v-for="link in links"
            :key="link.id"
            :label="link.label"
            :url="link.url"
            :button-type="'tertiary'"
            :linkTarget="'_blank'"
          />
        </div>
      </section>
      <section>
        <!-- Show options in second column -->
        <h2>Tilpass boligen</h2>
        <category-section
          v-for="category in OptionCategory"
          :key="category.id"
          :categoryId="category.id"
          :residenceId="residence.id"
        />
        <price-info :residenceId="residence.id" />
        <Calculator
          v-if="hasCalculator && hasLoadedAllData"
          :residenceId="residence.id"
        />
        <contact-form
          v-if="hasContactForm && hasLoadedAllData"
          :residenceId="residence.id"
        />
      </section>
    </section>

    <section
      v-else
      :class="{
        'l-detailspage--nomedia': residence.images.length === 0,
        'l-detailspage--media': residence.images.length > 0,
      }"
      class="l-detailspage"
    >
      <header
        class="l-detailspage__header u-border-bottom u-margin-bottom-small"
      >
        <meta
          property="og:type"
          content="product"
        />
        <meta
          property="og:title"
          :content="residence.name"
        />
        <meta
          property="og:url"
          :content="ogUrl"
        />
        <meta
          v-if="residence.images.length > 0"
          property="og:image"
          :content="residence.images[0].url"
        />
        <meta
          property="og:description"
          :content="residence.description"
        />
        <meta
          property="product:price.amount"
          :content="residence.totalPrice"
        />
        <meta
          property="product:price.currency"
          content="NOK"
        />
        <h1 class="u-h2 u-margin-bottom-none u-split">
          <span itemprop="name">{{ residence.name }}</span>
          <span>{{ priceInfo }}</span>
          <span
            itemprop="offers"
            itemscope
            itemtype="http://schema.org/Offer"
          >
            <span
              v-if="residence.availability.slug === 'for-sale'"
              itemprop="availability"
              content="https://schema.org/InStock"
            />
            <span
              itemprop="price"
              :content="residence.totalPrice"
            />
            <span
              itemprop="priceCurrency"
              content="NOK"
            />
          </span>
        </h1>
      </header>
      <Illustration
        @open="openLargeIllustrationsFromIllustrations()"
        :images="residence.images"
        :residence_id="residence.id"
        class="l-detailspage__media"
        itemprop="image"
      />
      <div class="l-detailspage__content">
        <p
          v-if="residence.description"
          class="u-push-bottom"
        >
          {{ residence.description }}
        </p>
        <Factlist
          v-if="hasLoadedAllData"
          :id="residence.id"
          :residencetype_id="residence.residencetype_id"
        />
        <spinner
          v-else
          class="c-spinner--page"
        />
        <Facilities
          v-if="residence.facilities.length > 0"
          :facilities="residence.facilities"
        />
        <section
          v-if="residence.longDescription"
          class="u-push-bottom"
        >
          <h3>Beskrivelse</h3>
          <div v-html="residence.longDescription" />
        </section>
        <button
          v-if="residence.images.length > 0"
          @click="openLargeIllustrationsFromButton"
          @keyup.enter="openLargeIllustrationsFromButton"
          class="c-btn c-btn--primary c-btn--margins u-push-bottom"
        >
          Se større plantegninger
        </button>
        <modal-illustration
          v-if="showLargeIllustrations && residence.images.length > 0"
          @close="toggleLargeIllustrations"
        >
          <Illustration :residence_id="residence.id" />
        </modal-illustration>
        <LinkButton
          v-for="file in files"
          :key="file.id"
          :label="file.title"
          :url="file.url"
          :button-type="'secondary'"
          :linkTarget="'_blank'"
          download
        />
        <LinkButton
          v-for="link in links"
          :key="link.id"
          :label="link.label"
          :url="link.url"
          :button-type="'tertiary'"
          :linkTarget="'_blank'"
        />
        <h3
          v-if="hasCalculator && hasLoadedAllData"
          class="u-push-top"
        >
          Lånekalkulator
        </h3>
        <Calculator
          v-if="hasCalculator && hasLoadedAllData"
          :residenceId="residence.id"
        />
        <contact-form
          v-if="hasContactForm && hasLoadedAllData"
          :residenceId="residence.id"
        />
      </div>
    </section>
  </div>
  <div
    v-else
    class="u-text-center"
  >
    <h2>Finnes ikke</h2>
    <p>Nå har du kommet til en side som ikke finnes.</p>
    <div class="unavailable__list">
      <router-link
        to="/"
        class="c-btn c-btn--primary c-btn--has-icon"
      >
        <icon-left
          class="c-btn__icon c-btn__icon--left c-btn__icon--alignment"
        />
        Gå tilbake til oversikten
      </router-link>
    </div>
  </div>
</template>

<script>
import { EventBus } from '@/event-bus.js';

// VUEX Models
import Residence from '@/models/Residence';
import OptionCategory from '@/models/OptionCategory';

// UI Components
import Factlist from '@/components/FactList.vue';
import Facilities from '@/components/Facilities.vue';
import LinkButton from '@/components/linkbutton.vue';
import Illustration from '@/components/Illustration.vue';
import CategorySection from '@/components/CategorySection.vue';
import Calculator from '@/components/Calculator.vue';
import PriceInfo from '@/components/PriceInfo.vue';
import ContactForm from '@/components/ContactForm.vue';
import Spinner from '@/components/Spinner.vue';
import ModalIllustration from '@/components/ModalIllustration.vue';
import IconLeft from '@/components/icons/IconLeft.vue';

export default {
  name: 'ResidencePage',
  components: {
    Factlist,
    Facilities,
    LinkButton,
    Illustration,
    CategorySection,
    Calculator,
    PriceInfo,
    ContactForm,
    Spinner,
    ModalIllustration,
    IconLeft,
  },
  data() {
    return {
      showModal: false,
      showLargeIllustrations: false,
    };
  },
  computed: {
    hasLoadedAllData() {
      if (this.residence.facts) {
        return true;
      }
      return false;
    },
    hasOptions() {
      return this.$store.state.entities.options.hasOptions;
    },
    hasCalculator() {
      const globalHasCalculator =
        this.$store.state.entities.calculatorsettings.hasCalculator;
      if (globalHasCalculator === true) {
        const availability = this.residence.availability;
        if (
          availability.slug === 'for-sale' ||
          availability.showCalculator === true
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    hasContactForm() {
      const globalHasContactForm =
        this.$store.state.entities.residences.hasContactForm;
      if (globalHasContactForm === true) {
        const availability = this.residence.availability;
        if (
          availability.slug === 'for-sale' ||
          availability.slug === 'coming' ||
          availability.showContactForm === true
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    route() {
      return this.$route.params.slug;
    },
    residence() {
      return Residence.query().withAll().where('slug', this.route).first();
    },
    OptionCategory() {
      if (this.hasOptions === true) {
        return OptionCategory.query()
          .where('count', value => value > 0)
          .get();
      } else {
        return false;
      }
    },
    priceInfo() {
      if (this.residence.availability.slug === 'for-sale') {
        return `${this.residence.totalPrice.toLocaleString('nb-NO')},–`;
      } else if (this.residence.availability.showPrice === true) {
        return `${
          this.residence.availability.name
        }: ${this.residence.totalPrice.toLocaleString('nb-NO')},–`;
      } else {
        return this.residence.availability.name;
      }
    },
    links() {
      if (
        this.residence.availability.slug === 'for-sale' ||
        this.residence.availability.showLinks === true
      ) {
        return this.residence.links;
      } else {
        return false;
      }
    },
    files() {
      if (
        this.residence.availability.slug === 'for-sale' ||
        this.residence.availability.showFiles === true
      ) {
        return this.residence.files;
      } else {
        return false;
      }
    },
    ogUrl() {
      const location = window.location.href;
      const sourceLocation = location.split('#')[0] + '#';
      return `${sourceLocation}/bolig/${this.residence.slug}`;
    },
  },
  created() {
    this.getResidence(this.residence.id);
    EventBus.$on('open-modal-illustration', () => {
      this.showLargeIllustrations = true;
    });
  },
  mounted() {
    this.registerPageView();
    this.scrollIntoView();
    this.updateViewed();
  },
  methods: {
    // Get missing residence data from server.
    getResidence(id) {
      if (this.residence.hasFacts === false) {
        const baseURL = window.__selector_settings__.sourceData.sourceURL;
        Residence.getResidence(id, baseURL, this.residence.hasImagesLoaded);
        Residence.commit(state => {
          state.hasImages.push(id);
        });
      }
    },
    // Helper function to check if an element is in view
    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      );
    },
    scrollIntoView() {
      // Get scrollHelper element and scroll window to that if it isn't in view
      const scrollHelper = document.getElementById('scrollHelper');

      if (this.isInViewport(scrollHelper) === false) {
        scrollHelper.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    },
    updateViewed() {
      // Update state with info about which residence the user viewed last in order to scroll the residence list to that residenceItem
      Residence.commit(state => {
        state.viewed = this.residence.slug;
      });
    },
    openLargeIllustrationsFromIllustrations() {
      this.showLargeIllustrations = true;
      this.registerEvent(
        'Illustrasjon: Åpne illustrasjoner i modal',
        'Modal',
        'open_illustration_from_illustration'
      );
    },
    openLargeIllustrationsFromButton() {
      this.showLargeIllustrations = true;
      this.registerEvent(
        'Knapp: Åpne illustrasjoner i modal',
        'Modal',
        'open_illustration_from_button'
      );
    },
    toggleLargeIllustrations() {
      this.showLargeIllustrations = !this.showLargeIllustrations;
    },

    // Register pageView
    registerPageView() {
      // Check if there is a Google Analytics Toggle on.
      if (window.__selector_settings__.trackers.gaCheck === true) {
        // Send tracking data to Google Analytics
        this.$gtag.pageview({
          page_path: `bolig/${this.residence.slug}`,
          page_title: this.residence.name,
        });
      }
      // Check if the Facebook pixel is active, if so send custom event.
      if (
        window.__selector_settings__.trackers.fbCheck === true &&
        typeof fbq === 'function'
      ) {
        fbq('track', 'ViewContent', {
          content_name: this.residence.name,
          content_category: 'Bolig',
          content_type: 'bolig',
        });
      }
    },
    // Register event in Google Analytics
    registerEvent(action, category, label) {
      if (window.__selector_settings__.trackers.gaCheck === true) {
        this.$gtag.event(action, {
          event_category: category,
          event_label: label,
        });
      }
    },
  },
};
</script>
